import { useCallback, useState } from 'react';

export function loadInitialState<T>(
  localStorageKey: string,
  initialState: T,
): T {
  const stored = localStorage.getItem(localStorageKey);
  if (stored !== null) {
    return JSON.parse(stored);
  } else {
    localStorage.setItem(localStorageKey, JSON.stringify(initialState));
    return initialState;
  }
}

/**
 * Same as useState, except the value persists to localStorage
 * using the key provided.
 * @param localStorageKey The key to save to.
 * @param initialState The initial state if not already saved.
 * @param forceInitialState Whether to force initial state to load over stored state
 */
export function useLocalStorage<T>(
  localStorageKey: string,
  initialState: T,
  forceInitialState = false,
): [T, (update: T) => void] {
  const [state, setState] = useState(
    forceInitialState
      ? initialState
      : loadInitialState(localStorageKey, initialState),
  );

  const updateState = useCallback(
    (update: T) => {
      setState(update);
      localStorage.setItem(localStorageKey, JSON.stringify(update));
    },
    [localStorageKey],
  );

  return [state, updateState];
}
